import { VoxelAsset, VoxelData } from '../../voxelTypes';
import { PALETTE } from '../../voxelColor';
import { getColors, addMiniBox, PotStyle } from './CatalogUtils';

export function getPlantCatalog(pot: PotStyle): VoxelAsset[] {
  const assets: VoxelAsset[] = [];
  const c = getColors({ potStyle: pot });

  // Helper to build a standard high-fidelity pot (mini-voxels)
  const buildPot = (voxels: VoxelData[]) => {
    // 3D round clay pot - solid, no holes!
    for (let x = -2.0; x <= 2.0; x += 0.5) {
      for (let z = -2.0; z <= 2.0; z += 0.5) {
        const dist = Math.hypot(x, z);
        if (dist <= 2.1 && dist >= 1.3) {
          // outer rim and side walls (y from 0 to 3.0, h = 7)
          addMiniBox(voxels, x, 0, z, 1, 7, 1, c.pot);
        } else if (dist < 1.3) {
          // bottom plate (y from 0 to 0.5, h = 1)
          addMiniBox(voxels, x, 0, z, 1, 1, 1, c.pot);
          // soil fill (y from 0.5 to 3.0, h = 6)
          addMiniBox(voxels, x, 0.5, z, 1, 6, 1, PALETTE.DIRT_DARK);
        }
      }
    }
  };

  // 1. Basil
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Leafy basil stalks
    addMiniBox(voxels, -1.0, 3.5, -1.0, 2, 3, 2, 0x477a33, 'foliage');
    addMiniBox(voxels, -1.5, 4.5, -0.5, 3, 2, 1, 0x548f3c, 'foliage'); // side leaf flares
    addMiniBox(voxels, -0.5, 5.5, -1.5, 1, 2, 3, 0x548f3c, 'foliage');
    assets.push({
      id: `plant_basil_${pot}`,
      name: `Potted Sweet Basil`,
      category: 'crop',
      description: 'Fragrant sweet basil with rounded green canopy leaves in a neat cottage pot.',
      voxels,
      dimensions: { x: 3, y: 4, z: 3 }
    });
  })();

  // 2. Bluebell
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Green thin stems arching out
    for (let y = 3.5; y <= 6.5; y += 0.5) {
      voxels.push({ x: -0.2, y, z: 0, color: 0x477a33, scale: 0.5 });
      voxels.push({ x: 0.2, y, z: 0, color: 0x477a33, scale: 0.5 });
    }
    // Drooping blue bells (emissive blue)
    voxels.push({ x: -0.8, y: 5.5, z: 0.2, color: PALETTE.FLOWER_BLUE, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: 0.8, y: 5.0, z: -0.2, color: PALETTE.FLOWER_BLUE, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: 0, y: 6.8, z: 0.4, color: PALETTE.FLOWER_BLUE, scale: 0.5, materialType: 'emissive' });
    assets.push({
      id: `plant_bluebell_${pot}`,
      name: `Potted Bluebell`,
      category: 'crop',
      description: 'Drooping bell-shaped wildflowers of rich sapphire-blue hue.',
      voxels,
      dimensions: { x: 3, y: 4.5, z: 3 }
    });
  })();

  // 3. Chamomile
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Feathery light green leaves
    addMiniBox(voxels, -1.5, 3.5, -1.5, 3, 2, 3, 0x5c8a40, 'foliage');
    // Stalks and tiny flowers (white petals, yellow eye center)
    const stems = [
      { x: -0.8, y: 5.0, z: -0.8 },
      { x: 0.8, y: 5.5, z: -0.4 },
      { x: 0, y: 6.2, z: 0.6 }
    ];
    stems.forEach(st => {
      voxels.push({ x: st.x, y: st.y, z: st.z, color: 0xffffff, scale: 0.5 }); // white petals
      voxels.push({ x: st.x, y: st.y + 0.3, z: st.z, color: PALETTE.FLOWER_YELLOW, scale: 0.3 }); // center golden eye
    });
    assets.push({
      id: `plant_chamomile_${pot}`,
      name: `Potted Chamomile`,
      category: 'crop',
      description: 'Dainty white chamomile daisies with cozy feathery sweet-scented foliage.',
      voxels,
      dimensions: { x: 3, y: 4.5, z: 3 }
    });
  })();

  // 4. Cherry Tomato
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Climbing structural wood stake
    for (let y = 3.5; y <= 7.5; y += 0.5) {
      voxels.push({ x: 0, y, z: 0, color: PALETTE.WOOD_OAK_DARK, scale: 0.3 });
    }
    // Woven tomato vines
    addMiniBox(voxels, -1.5, 4.0, -1.5, 3, 4, 3, 0x427529, 'foliage');
    // Glowing red cherry tomatoes (emissive red)
    voxels.push({ x: -1.0, y: 4.5, z: 1.0, color: PALETTE.FLOWER_RED, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: 1.0, y: 5.5, z: -1.0, color: PALETTE.FLOWER_RED, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: -0.4, y: 6.5, z: 0.8, color: PALETTE.FLOWER_RED, scale: 0.4, materialType: 'emissive' });
    assets.push({
      id: `plant_tomato_${pot}`,
      name: `Potted Cherry Tomato`,
      category: 'crop',
      description: 'A miniature climbing garden vine loaded with sweet glowing red cherry tomatoes.',
      voxels,
      dimensions: { x: 3, y: 5, z: 3 }
    });
  })();

  // 5. Chives
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Spiky vertical chive needles (fine scale)
    for (let x = -1.0; x <= 1.0; x += 0.4) {
      for (let z = -1.0; z <= 1.0; z += 0.4) {
        const h = 4.5 + Math.random() * 2.0;
        for (let y = 3.5; y <= h; y += 0.5) {
          voxels.push({ x, y, z, color: 0x4d8a29, scale: 0.3 });
        }
        // Purple pom-pom flower top
        voxels.push({ x, y: h + 0.1, z, color: PALETTE.FLOWER_PURPLE, scale: 0.5, materialType: 'emissive' });
      }
    }
    assets.push({
      id: `plant_chives_${pot}`,
      name: `Potted Chives`,
      category: 'crop',
      description: 'Spiky grass-like onion chives topped with glowing lavender pom-pom flowerheads.',
      voxels,
      dimensions: { x: 3, y: 4.5, z: 3 }
    });
  })();

  // 6. Daffodil
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Strappy sword green leaves
    addMiniBox(voxels, -1.0, 3.5, -1.0, 2, 3, 2, 0x47702a, 'foliage');
    // Trumpet daffodils flowers (yellow petals, orange center)
    const stems = [
      { x: -0.6, y: 5.5, z: -0.6 },
      { x: 0.6, y: 5.8, z: 0.4 }
    ];
    stems.forEach(s => {
      voxels.push({ x: s.x, y: s.y, z: s.z, color: PALETTE.FLOWER_YELLOW, scale: 0.6 }); // yellow cup petals
      voxels.push({ x: s.x, y: s.y, z: s.z + 0.2, color: PALETTE.PUMPKIN_ORANGE, scale: 0.3, materialType: 'emissive' }); // orange trumpet
    });
    assets.push({
      id: `plant_daffodil_${pot}`,
      name: `Potted Daffodil`,
      category: 'crop',
      description: 'Classic yellow spring trumpets framed by deep-green strappy leaves.',
      voxels,
      dimensions: { x: 3, y: 4.5, z: 3 }
    });
  })();

  // 7. Daisy
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Star daisy clusters (white petals, yellow centers)
    addMiniBox(voxels, -1.5, 3.5, -1.5, 3, 2, 3, 0x437a28, 'foliage');
    for (let dx = -1.0; dx <= 1.0; dx += 1.0) {
      for (let dz = -1.0; dz <= 1.0; dz += 1.0) {
        if (Math.abs(dx + dz) !== 0) {
          voxels.push({ x: dx, y: 5.0, z: dz, color: 0xffffff, scale: 0.6 });
          voxels.push({ x: dx, y: 5.3, z: dz, color: PALETTE.FLOWER_YELLOW, scale: 0.3 });
        }
      }
    }
    assets.push({
      id: `plant_daisy_${pot}`,
      name: `Potted Daisy`,
      category: 'crop',
      description: 'Cheery white lawn daisies showing flat golden radiating faces.',
      voxels,
      dimensions: { x: 3, y: 4.2, z: 3 }
    });
  })();

  // 8. Fern
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Massive arching fan leaflets (forest green)
    addMiniBox(voxels, -1.5, 3.5, -1.5, 3, 2, 3, PALETTE.MOSS, 'foliage');
    // Arching fan segments
    addMiniBox(voxels, -3.0, 4.0, -1.5, 2, 1, 3, PALETTE.GRASS_BRIGHT, 'foliage'); // left fan
    addMiniBox(voxels, 1.5, 4.0, -1.5, 2, 1, 3, PALETTE.GRASS_BRIGHT, 'foliage'); // right fan
    addMiniBox(voxels, -1.5, 4.0, -3.0, 3, 1, 2, PALETTE.GRASS_LUSH, 'foliage'); // front fan
    addMiniBox(voxels, -1.5, 4.0, 1.5, 3, 1, 2, PALETTE.GRASS_LUSH, 'foliage'); // back fan
    assets.push({
      id: `plant_fern_${pot}`,
      name: `Potted Fern`,
      category: 'crop',
      description: 'A dense, arching green wood-fern with complex feathered foliage plumes.',
      voxels,
      dimensions: { x: 4.5, y: 3.5, z: 4.5 }
    });
  })();

  // 9. Foxglove
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Broad velvet green leaves
    addMiniBox(voxels, -1.5, 3.5, -1.5, 3, 1.5, 3, 0x3d6624, 'foliage');
    // Stately tall spikes (pink speckled bells)
    for (let y = 4.5; y <= 8.5; y += 0.5) {
      voxels.push({ x: 0, y, z: 0, color: 0x47702a, scale: 0.4 }); // central stalk
      // pink tubular bells surrounding the stalk
      const col = y > 7.0 ? PALETTE.FLOWER_WHITE : PALETTE.FLOWER_PURPLE;
      voxels.push({ x: -0.4, y: y - 0.1, z: 0.2, color: col, scale: 0.5, materialType: 'emissive' });
      voxels.push({ x: 0.4, y: y - 0.1, z: -0.2, color: col, scale: 0.5, materialType: 'emissive' });
    }
    assets.push({
      id: `plant_foxglove_${pot}`,
      name: `Potted Foxglove`,
      category: 'crop',
      description: 'A majestic tall spire loaded with freckled trumpet-pink foxglove bells.',
      voxels,
      dimensions: { x: 3, y: 6, z: 3 }
    });
  })();

  // 10. Garden Pea
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Little twig lattice panel
    for (let y = 3.5; y <= 7.5; y += 1.0) {
      voxels.push({ x: -1.0, y, z: 0, color: PALETTE.WOOD_OAK_DARK, scale: 0.2 });
      voxels.push({ x: 1.0, y, z: 0, color: PALETTE.WOOD_OAK_DARK, scale: 0.2 });
      voxels.push({ x: -1.0, y: y + 0.5, z: 0, color: PALETTE.WOOD_OAK_DARK, scale: 1.0, scaleY: 0.1, scaleZ: 0.1 }); // cross bar
    }
    // Delicate curly vines running up
    addMiniBox(voxels, -1.5, 4.0, -1.0, 3, 4, 2, 0x4d8236, 'foliage');
    // Little white blossoms and tiny green peas hanging down
    voxels.push({ x: -0.8, y: 5.5, z: 0.6, color: 0xffffff, scale: 0.4 }); // flower
    voxels.push({ x: 0.8, y: 6.5, z: -0.6, color: 0x54a32b, scale: 0.5 }); // pea pod
    assets.push({
      id: `plant_garden_pea_${pot}`,
      name: `Potted Garden Pea`,
      category: 'crop',
      description: 'A trellised climbing pea vine holding tender green pods and tiny white blossoms.',
      voxels,
      dimensions: { x: 3.5, y: 5, z: 3 }
    });
  })();

  // 11. Hydrangea
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Broad support leaf stalks
    addMiniBox(voxels, -1.5, 3.5, -1.5, 3, 2, 3, 0x315c1e, 'foliage');
    // Giant fluffy pom-pom globes of flowers (blue/pink/purple blends)
    const bloomColors = [0x3a8ce8, 0x7642bf, 0xd44ca7, 0x2ec7bf];
    bloomColors.forEach((color, idx) => {
      const bx = (idx % 2 === 0 ? -1.0 : 1.0) * 0.8;
      const bz = (idx < 2 ? -1.0 : 1.0) * 0.8;
      const by = 5.0 + Math.random() * 0.8;
      // build a puffy mini-sphere
      for (let x = -1.0; x <= 1.0; x += 0.5) {
        for (let y = -1.0; y <= 1.0; y += 0.5) {
          for (let z = -1.0; z <= 1.0; z += 0.5) {
            if (x*x + y*y + z*z <= 1.2) {
              voxels.push({ x: bx + x, y: by + y, z: bz + z, color, scale: 0.4 });
            }
          }
        }
      }
    });
    assets.push({
      id: `plant_hydrangea_${pot}`,
      name: `Potted Hydrangea`,
      category: 'crop',
      description: 'Massive, fluffy pom-pom blossoms blending pastel blue, lavender, and soft pink.',
      voxels,
      dimensions: { x: 4, y: 4.5, z: 4 }
    });
  })();

  // 12. Iris
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Tall sword-shaped gray-green leaves
    for (let y = 3.5; y <= 6.5; y += 0.5) {
      voxels.push({ x: -0.6, y, z: -0.4, color: 0x567a63, scale: 0.4 });
      voxels.push({ x: 0.6, y, z: 0.4, color: 0x567a63, scale: 0.4 });
    }
    // Intricate purple flags with yellow drops (iris)
    voxels.push({ x: -0.5, y: 7.0, z: -0.2, color: PALETTE.FLOWER_PURPLE, scale: 0.6 });
    voxels.push({ x: -0.5, y: 6.8, z: 0.1, color: PALETTE.FLOWER_YELLOW, scale: 0.3, materialType: 'emissive' });
    voxels.push({ x: 0.5, y: 7.5, z: 0.2, color: PALETTE.FLOWER_PURPLE, scale: 0.6 });
    voxels.push({ x: 0.5, y: 7.3, z: 0.5, color: PALETTE.FLOWER_YELLOW, scale: 0.3, materialType: 'emissive' });
    assets.push({
      id: `plant_iris_${pot}`,
      name: `Potted Iris`,
      category: 'crop',
      description: 'Elegant bearded iris flags of deep royal purple with glowing gold falls.',
      voxels,
      dimensions: { x: 3, y: 5, z: 3 }
    });
  })();

  // 13. Lavender
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Tufted gray-green narrow leaves
    addMiniBox(voxels, -1.2, 3.5, -1.2, 2.4, 2, 2.4, 0x47634f, 'foliage');
    // Multiple dense vertical lavender spikes (purple)
    const spikes = [
      { x: -0.8, z: -0.8 }, { x: 0.8, z: -0.8 },
      { x: -0.8, z: 0.8 }, { x: 0.8, z: 0.8 },
      { x: 0, z: 0 }
    ];
    spikes.forEach((sp, idx) => {
      const h = 6.0 + (idx % 2) * 1.0;
      for (let y = 5.0; y <= h; y += 0.5) {
        voxels.push({ x: sp.x, y, z: sp.z, color: PALETTE.FLOWER_PURPLE, scale: 0.4, materialType: 'emissive' });
      }
    });
    assets.push({
      id: `plant_lavender_${pot}`,
      name: `Potted Lavender`,
      category: 'crop',
      description: 'Fragrant purple lavender spires standing tall over tufted gray-green grass clumps.',
      voxels,
      dimensions: { x: 3, y: 4.5, z: 3 }
    });
  })();

  // 14. Lemon Balm
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Textured mint-like serrated leaves (pale yellow-green)
    addMiniBox(voxels, -1.5, 3.5, -1.5, 3, 2.5, 3, 0x6e9a38, 'foliage');
    // Tiny white flower whorls along stems
    voxels.push({ x: -0.8, y: 4.8, z: 0, color: 0xffffff, scale: 0.3 });
    voxels.push({ x: 0.8, y: 5.2, z: -0.4, color: 0xffffff, scale: 0.3 });
    assets.push({
      id: `plant_lemon_balm_${pot}`,
      name: `Potted Lemon Balm`,
      category: 'crop',
      description: 'Textured, citrus-scented lime-green leaves holding tiny delicate white florets.',
      voxels,
      dimensions: { x: 3, y: 4, z: 3 }
    });
  })();

  // 15. Marigold
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Finely cut tooth leaves
    addMiniBox(voxels, -1.5, 3.5, -1.5, 3, 2, 3, 0x3d5e23, 'foliage');
    // Pom-pom flowers of rich gold/orange/bronze
    const blooms = [
      { x: -0.8, y: 5.0, z: -0.8, col: PALETTE.PUMPKIN_ORANGE },
      { x: 0.8, y: 5.2, z: -0.4, col: PALETTE.FLOWER_YELLOW },
      { x: 0, y: 5.8, z: 0.6, col: PALETTE.PUMPKIN_DARK }
    ];
    blooms.forEach(b => {
      voxels.push({ x: b.x, y: b.y, z: b.z, color: b.col, scale: 0.6, materialType: 'emissive' });
    });
    assets.push({
      id: `plant_marigold_${pot}`,
      name: `Potted Marigold`,
      category: 'crop',
      description: 'Velvety puff marigold pom-poms blending glowing saffron gold and rich copper orange.',
      voxels,
      dimensions: { x: 3, y: 4.2, z: 3 }
    });
  })();

  // 16. Mint
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Dense mint leaves (emerald pointed leaves)
    addMiniBox(voxels, -1.5, 3.5, -1.5, 3, 3, 3, 0x24662d, 'foliage');
    // leafy tips protruding
    addMiniBox(voxels, -2.0, 4.5, -0.5, 4, 1, 1, 0x31853d, 'foliage');
    addMiniBox(voxels, -0.5, 5.5, -2.0, 1, 1, 4, 0x31853d, 'foliage');
    assets.push({
      id: `plant_mint_${pot}`,
      name: `Potted Peppermint`,
      category: 'crop',
      description: 'Vigorous pointed peppermint leaves forming a dense, refreshing green bush canopy.',
      voxels,
      dimensions: { x: 3, y: 4, z: 3 }
    });
  })();

  // 17. Nasturtium
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Rounded shield-like leaves (flat circles)
    for (let x = -1.5; x <= 1.5; x += 1.0) {
      for (let z = -1.5; z <= 1.5; z += 1.0) {
        voxels.push({ x, y: 4.2, z, color: 0x568037, scale: 0.5, scaleY: 0.1 }); // shield leaves
      }
    }
    // Bright orange blooms with tail spurs (pumpkin orange)
    voxels.push({ x: -1.0, y: 4.5, z: 0.8, color: PALETTE.PUMPKIN_ORANGE, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: 1.0, y: 4.8, z: -0.8, color: PALETTE.PUMPKIN_ORANGE, scale: 0.5, materialType: 'emissive' });
    voxels.push({ x: 0, y: 5.2, z: 0.2, color: PALETTE.FLOWER_RED, scale: 0.4 });
    assets.push({
      id: `plant_nasturtium_${pot}`,
      name: `Potted Nasturtium`,
      category: 'crop',
      description: 'Water-shield lilypad leaves holding bright trailing spurred scarlet-orange nasturtiums.',
      voxels,
      dimensions: { x: 3, y: 4, z: 3 }
    });
  })();

  // 18. Pansy
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Flat velvety heart-leaves
    addMiniBox(voxels, -1.2, 3.5, -1.2, 2.4, 1.5, 2.4, 0x3c6126, 'foliage');
    // Stately pansy faces with deep purple center and golden rims
    const faces = [
      { x: -0.6, y: 4.5, z: -0.6, colCenter: PALETTE.FLOWER_PURPLE, colRim: PALETTE.FLOWER_YELLOW },
      { x: 0.6, y: 4.8, z: 0.4, colCenter: PALETTE.FLOWER_BLUE, colRim: 0xffffff }
    ];
    faces.forEach(f => {
      voxels.push({ x: f.x, y: f.y, z: f.z, color: f.colCenter, scale: 0.6 }); // velvet heart center
      voxels.push({ x: f.x, y: f.y + 0.1, z: f.z + 0.2, color: f.colRim, scale: 0.4 }); // outer accent rim petals
    });
    assets.push({
      id: `plant_pansy_${pot}`,
      name: `Potted Pansy`,
      category: 'crop',
      description: 'Velvety, face-like heart petals blending violet purple and brilliant yellow rims.',
      voxels,
      dimensions: { x: 3, y: 4, z: 3 }
    });
  })();

  // 19. Parsley
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Crinkly divided leaves in tight tufted clumps
    addMiniBox(voxels, -1.5, 3.5, -1.5, 3, 2, 3, 0x2f521f, 'foliage');
    addMiniBox(voxels, -1.8, 4.5, -1.8, 3.6, 1.5, 3.6, 0x3f6e2b, 'foliage'); // fluffy curly crown
    assets.push({
      id: `plant_parsley_${pot}`,
      name: `Potted Parsley`,
      category: 'crop',
      description: 'Fluffy tufted mounds of highly textured curly dark green parsley garnish.',
      voxels,
      dimensions: { x: 3, y: 4, z: 3 }
    });
  })();

  // 20. Rosemary
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Stiff silver-green needle stalks
    for (let x = -1.0; x <= 1.0; x += 0.8) {
      for (let z = -1.0; z <= 1.0; z += 0.8) {
        const h = 5.0 + Math.random() * 1.5;
        for (let y = 3.5; y <= h; y += 0.5) {
          voxels.push({ x, y, z, color: 0x465c4d, scale: 0.4 }); // silver needles
        }
        // Pale blue flowers on stem
        voxels.push({ x: x + 0.1, y: h - 0.5, z: z, color: 0x8abdf2, scale: 0.3, materialType: 'emissive' });
      }
    }
    assets.push({
      id: `plant_rosemary_${pot}`,
      name: `Potted Rosemary`,
      category: 'crop',
      description: 'Stiff, aromatic woody pine needles dusted in a mist of tiny pale-blue herb blooms.',
      voxels,
      dimensions: { x: 3, y: 4.2, z: 3 }
    });
  })();

  // 21. Snapdragon
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    addMiniBox(voxels, -1.2, 3.5, -1.2, 2.4, 1.5, 2.4, 0x3a5c23, 'foliage');
    // Stately tall snapping flower tubes (crimson red / yellow lip)
    for (let y = 4.5; y <= 8.5; y += 0.5) {
      voxels.push({ x: 0, y, z: 0, color: 0x3a5c23, scale: 0.4 }); // stalk
      // jaw florets
      voxels.push({ x: -0.3, y: y - 0.1, z: 0.1, color: PALETTE.FLOWER_RED, scale: 0.5 });
      voxels.push({ x: 0.3, y: y - 0.1, z: -0.1, color: PALETTE.FLOWER_YELLOW, scale: 0.4, materialType: 'emissive' });
    }
    assets.push({
      id: `plant_snapdragon_${pot}`,
      name: `Potted Snapdragon`,
      category: 'crop',
      description: 'Tall spikes of jaw-like colorful vertical flower tubes of crimson and sunshine gold.',
      voxels,
      dimensions: { x: 3, y: 6, z: 3 }
    });
  })();

  // 22. Strawberry
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Green leafy runners spilling over edge
    addMiniBox(voxels, -1.5, 3.5, -1.5, 3, 2, 3, 0x47702a, 'foliage');
    // Hanging runners spilling down the pot sides
    addMiniBox(voxels, -2.5, 2.5, -0.5, 1, 2, 1, 0x47702a, 'foliage');
    addMiniBox(voxels, 1.5, 2.5, 0.5, 1, 2, 1, 0x47702a, 'foliage');
    // Red ripe strawberries hanging down (emissive red)
    voxels.push({ x: -1.3, y: 2.2, z: -0.2, color: PALETTE.FLOWER_RED, scale: 0.4, materialType: 'emissive' });
    voxels.push({ x: 1.3, y: 2.0, z: 0.3, color: PALETTE.FLOWER_RED, scale: 0.4, materialType: 'emissive' });
    // Small white flowers
    voxels.push({ x: 0, y: 4.8, z: 0.8, color: 0xffffff, scale: 0.4 });
    assets.push({
      id: `plant_strawberry_${pot}`,
      name: `Potted Strawberry`,
      category: 'crop',
      description: 'Lush green clover runners spilling over the pot rim holding small ripe glowing berries.',
      voxels,
      dimensions: { x: 3, y: 4, z: 3 }
    });
  })();

  // 23. Sunflower
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Stately tall sunflower stem & leaves
    for (let y = 3.5; y <= 9.5; y += 0.5) {
      voxels.push({ x: 0, y, z: 0, color: 0x416e25, scale: 0.5 });
    }
    // Broad side fan leaves
    voxels.push({ x: -0.8, y: 5.5, z: 0, color: 0x416e25, scale: 0.5, scaleX: 1.5 });
    voxels.push({ x: 0.8, y: 7.0, z: 0, color: 0x416e25, scale: 0.5, scaleX: 1.5 });
    // Giant sunflower head disc (radiating yellow around chocolate brown core)
    // facing forward (z = 0.5 offset)
    for (let hx = -1.5; hx <= 1.5; hx += 0.5) {
      for (let hy = -1.5; hy <= 1.5; hy += 0.5) {
        const distSq = (hx*hx)/2.25 + (hy*hy)/2.25;
        if (distSq <= 1.0) {
          const isCore = distSq < 0.3;
          const color = isCore ? PALETTE.SUNFLOWER_CENTER : PALETTE.FLOWER_YELLOW;
          voxels.push({ x: hx, y: 9.5 + hy, z: 0.4, color, scale: 0.5, materialType: isCore ? 'opaque' : 'emissive' });
        }
      }
    }
    assets.push({
      id: `plant_sunflower_${pot}`,
      name: `Potted Sunflower`,
      category: 'crop',
      description: 'A stately tall sunflower topped with a massive radiating yellow-and-chocolate seed face.',
      voxels,
      dimensions: { x: 3.5, y: 7.5, z: 3.5 }
    });
  })();

  // 24. Thyme
  (() => {
    const voxels: VoxelData[] = [];
    buildPot(voxels);
    // Dense mossy tiny green thyme mat mound (silver green)
    addMiniBox(voxels, -1.8, 3.5, -1.8, 3.6, 1.8, 3.6, 0x4d6347, 'foliage');
    // spilling over edge
    addMiniBox(voxels, -2.3, 3.0, -1.0, 1, 1, 2, 0x4d6347, 'foliage');
    addMiniBox(voxels, 1.3, 3.0, 1.0, 1, 1, 2, 0x4d6347, 'foliage');
    assets.push({
      id: `plant_thyme_${pot}`,
      name: `Potted English Thyme`,
      category: 'crop',
      description: 'A creeping herbal mat of minute silver-green leaves overflowing the pot rim.',
      voxels,
      dimensions: { x: 3.2, y: 4, z: 3.2 }
    });
  })();

  return assets;
}
